/* eslint-disable radix */
import React, { useCallback, useEffect, useState } from 'react';
import { useModal } from 'react-modal-hook';
import dayjs from 'dayjs';
import { FormikProps } from 'formik';
import debounce from 'lodash/debounce';
import { AlertContent, Button, Empty, IUserProps, Label, LayoutSidebar, ModalFactory, PayeeInputItem, PaymentInputSidebar, PayoutLimit, SectionHeader, Switch, Text, TooltipIcon, TooltipWrapper } from 'components';
import { ISidebarNotification } from 'components/Sidebar/components/Sidebar';
import { DEFAULT_ORG_ADMIN_JOB_TITLE, MakePaymentNotification } from 'config/const';
import { Appearance } from 'design';
import { SelectUser } from 'modals';
import { ApiQueryOperator, EmployeeType, IEmployee, IPayee, ISiteRolePolicy, ISubmitPaymentPayload } from 'types';
import { capitalize, centsToDollarsDisplay, formatJobTitle, isEmployeePayable } from 'utils';
import * as API from '../../../../api';
import { MakePaymentHeader } from '../../components';
import { MPContent, PayeesContainer } from '../../styles';
import { MakePaymentProps } from '../../types';

interface IPaymentInputProps extends MakePaymentProps {
  payees: IPayee[];
  isPaymentUpload: boolean;
  cashEnabled: boolean;
  setCashEnabled: (enabled: boolean) => void;
  tipPoolLimit?: number;
  setTipPoolLimit: (tipPoolLimit: number | undefined) => void;
  employeeTipLimit: number;
  rolePolicy?: ISiteRolePolicy;
  formik: FormikProps<ISubmitPaymentPayload>;
  showOverdraftWarning: boolean;
  overdraftText: string;
  overdraftContinue: boolean;
  strictTipPool: boolean;
}

const transformEmployee = (employee: IEmployee, disabledReason: string): IUserProps => {
  const { sites } = employee;
  const siteText: string = (() => {
    if (sites.length > 1 || sites.length === 0) {
      if (employee.type === EmployeeType.ADMIN) return 'All Sites';
      return `${sites.length} Sites`;
    }
    if (sites.length === 1) {
      return sites[0].name;
    }
    return '';
  })();
  const roleText: string = (() => {
    if (employee.type === EmployeeType.ADMIN) return formatJobTitle(DEFAULT_ORG_ADMIN_JOB_TITLE);

    const roles = sites.map((site) => site.role);
    const uniqueRoles = new Set(roles);

    if (uniqueRoles.size > 1 || uniqueRoles.size === 0) {
      return `${sites.length} Roles`;
    }
    if (uniqueRoles.size === 1) {
      return capitalize(roles[0]);
    }
    return '';
  })();

  return {
    id: employee.id,
    username: employee.name,
    image: employee.user?.avatarUrl,
    hasPaymentMethod: !!employee.user?.card,
    role: roleText,
    site: siteText,
    disabled: !!disabledReason,
    disabledReason,
  };
};

const pageSize = 4;

const PaymentInput = ({
  onBack,
  onNext,
  onAddPayees,
  onRemovePayee,
  currentEmployee,
  currentSiteId,
  businessDate,
  payees,
  pageHeaderContent,
  cashEnabled,
  setCashEnabled,
  tipPoolLimit,
  setTipPoolLimit,
  employeeTipLimit,
  totals,
  formik,
  showOverdraftWarning,
  overdraftText,
  overdraftContinue,
  isPaymentUpload,
  rolePolicy,
  strictTipPool = false,
}: IPaymentInputProps) => {
  const [deletePayeeIndex, setDeletePayeeIndex] = useState<number>();
  const [limit, setLimit] = useState<number>(pageSize);
  const [searchName, setSearchName] = useState<string>('');
  const [searchedPayees, setSearchedPayees] = useState<IEmployee[]>();
  const { gross, cash } = totals;

  // Remaining Payout
  const remainingTipPool = (tipPoolLimit || 0) - gross;

  useEffect(() => {
    if (isPaymentUpload) {
      if (cash > 0) setCashEnabled(true);
      // manually set uploaded tip values to `touched` so each field gets validated
      formik.setTouched({ payees: payees.map(() => ({ tipAmount: true, cash: cash > 0 })) });
    }
  }, [isPaymentUpload]);

  const fetchEmployees = (searchName?: string, limit = pageSize) => {
    const q = searchName ? [{ key: 'name', operator: ApiQueryOperator.like, value: searchName }] : [];
    API.Sites.listEmployees(currentSiteId, { q, sort: 'name', limit: limit + pageSize + 1 })
      .then((res) => setSearchedPayees(res.body.data))
      .catch(() => setSearchedPayees([]));
  };

  const onAddForgottenEmployeeSelect = (employeeToAdd: IEmployee) => {
    if (employeeToAdd) {
      onAddPayees([employeeToAdd]);
    }
  };

  const isEmployeeDisabled = (employee: IEmployee) => {
    if (payees.some((py) => py.id === employee.id)) {
      return 'Employee has already been added';
    }
    if (rolePolicy && !isEmployeePayable(employee, rolePolicy, currentEmployee)) {
      return 'You are not allowed to issue tips to this user.';
    }
    return '';
  };

  const [showAddPayeeModal, hideAddPayeeModal] = useModal(() => {
    const delayedNameChange = debounce((changedName: string) => {
      setLimit(pageSize);
      setSearchName(changedName);
      fetchEmployees(changedName, pageSize);
    }, 500);

    const loadMore = () => {
      setLimit(limit + pageSize);
      fetchEmployees(searchName, limit + pageSize);
    };

    const onClose = () => {
      setLimit(pageSize);
      setSearchName('');
      setSearchedPayees(undefined);
      hideAddPayeeModal();
    };

    return (
      <ModalFactory customModal onClose={hideAddPayeeModal} isActive>
        <SelectUser
          title="Add User"
          users={searchedPayees?.map((emp) => transformEmployee(emp, isEmployeeDisabled(emp)))}
          isActive
          isLoading={!searchedPayees}
          onSelect={(id) => {
            onAddForgottenEmployeeSelect(searchedPayees?.find((emp) => emp.id === id));
            onClose();
          }}
          onNameChange={delayedNameChange}
          loadMore={loadMore}
          onClose={onClose}
          limit={limit}
        />
      </ModalFactory>
    );
  }, [limit, searchName, searchedPayees, isEmployeeDisabled]);

  const onAddForgottenEmployeeBtnClick = () => {
    fetchEmployees();
    showAddPayeeModal();
  };

  // Remove User Modal
  const [showRemovePayeeModal, hideRemovePayeeModal] = useModal(() => {
    const handleRemovePayee = () => {
      onRemovePayee(deletePayeeIndex);
      hideRemovePayeeModal();
    };

    return (
      <ModalFactory customModal onClose={hideRemovePayeeModal} isActive>
        <AlertContent title="Remove payee?" message="Are you sure you want to remove this payee?" leftActionLabel="Cancel" rightActionLabel="Remove" onLeftAction={hideRemovePayeeModal} onRightAction={handleRemovePayee} />
      </ModalFactory>
    );
  }, [deletePayeeIndex]);

  const handleRemovePayee = useCallback(
    (index: number) => {
      setDeletePayeeIndex(index);
      showRemovePayeeModal();
    },
    [showRemovePayeeModal]
  );

  const disableNext = !!formik.errors.payees || formik.isValidating || (strictTipPool && remainingTipPool !== 0) || !overdraftContinue;

  const buildSidebarNotifications = (): ISidebarNotification[] => {
    const notifications: ISidebarNotification[] = [];

    if (tipPoolLimit !== undefined) {
      let payoutLimitNotification: ISidebarNotification;

      if (gross < tipPoolLimit) {
        // Payout Limit Not Met
        payoutLimitNotification = {
          ...MakePaymentNotification.payoutLimitNotMet,
          message: (
            <>
              <Text size="small">
                There is a remaining limit of <strong>{centsToDollarsDisplay(tipPoolLimit - gross)}</strong>.
              </Text>
              <Text size="small">Increase your payouts or decrease the limit.</Text>
            </>
          ),
        };
      } else if (gross > tipPoolLimit) {
        // Payout Limit Exceeded
        payoutLimitNotification = {
          ...MakePaymentNotification.payoutLimitExceeded,
          message: (
            <>
              <Text size="small">
                You have exceeded the payout limit by <strong>{centsToDollarsDisplay(gross - tipPoolLimit)}</strong>.
              </Text>
              <Text size="small">Increase the limit, or change your payouts.</Text>
            </>
          ),
        };
      }

      if (payoutLimitNotification) {
        notifications.push({
          ...payoutLimitNotification,
          appearance: strictTipPool ? Appearance.DANGER : Appearance.WARNING,
          preventSubmit: strictTipPool,
        });
      }
    }

    // Payees Error
    if (typeof formik.errors?.payees === 'string') {
      notifications.push({ ...MakePaymentNotification.payeeError, message: formik.errors.payees as string });
    }

    // Overdraft Warning
    if (showOverdraftWarning) {
      notifications.push({ ...MakePaymentNotification.overdraftWarning, message: overdraftText, preventSubmit: !overdraftContinue });
    }

    return notifications;
  };

  return (
    <LayoutSidebar
      content={
        <>
          <MakePaymentHeader pageHeaderContent={pageHeaderContent} />

          <MPContent>
            <SectionHeader title={`Payees (${payees.length})`}>
              <TooltipWrapper className="cash-toggle">
                <Label>Cash Payouts</Label>
                <TooltipIcon id={'cash-tooltip'} place="top" iconSize={20} iconMarginLeft={0}>
                  Record payments made to your employees in cash
                </TooltipIcon>
                <Switch checked={cashEnabled} onChange={() => setCashEnabled(!cashEnabled)} data-cy="cash-switch" />
              </TooltipWrapper>
              <PayoutLimit
                total={gross}
                limit={tipPoolLimit}
                isStrict={strictTipPool}
                onChange={(value) => {
                  setTipPoolLimit(value === undefined ? undefined : parseInt(value));
                }}
              />
              <Button label="Add User" appearance="white" size="small" data-cy="add-forgotten-employee-btn" onClick={onAddForgottenEmployeeBtnClick} />
            </SectionHeader>

            <PayeesContainer data-cy="payments-input-list">
              {payees.length === 0 ? (
                <Empty container containerHeight={250} icon="People" message="No Payees" buttonLabel="Add User" onClick={onAddForgottenEmployeeBtnClick} />
              ) : (
                payees.map((payee: IPayee, index: number) => (
                  <PayeeInputItem
                    key={`payee_${payee.id}`}
                    hasCash={cashEnabled}
                    siteId={currentSiteId}
                    removeDisabled={payees.length === 1}
                    onRemove={handleRemovePayee}
                    payee={payee}
                    index={index}
                    setFieldValue={formik.setFieldValue}
                    setFieldTouched={formik.setFieldTouched}
                    maxTipAmount={employeeTipLimit}
                  />
                ))
              )}
            </PayeesContainer>
          </MPContent>
        </>
      }
      sidebar={<PaymentInputSidebar onBack={onBack} onContinue={onNext} businessDate={dayjs(businessDate, 'MM-DD-YYYY')} continueDisabled={disableNext} showCash={cashEnabled} totals={totals} notifications={buildSidebarNotifications()} />}
    />
  );
};

export default PaymentInput;
